/**
 * Product QA Utility
 * Validates that all products displayed as "directly addable to cart" have correct pricing
 * and variant configuration to prevent cart/price mismatches.
 */

import { fetchProducts, ShopifyProduct } from "@/lib/shopify";

export interface QAIssue {
  productId: string;
  productHandle: string;
  productTitle: string;
  issueType: "PRICE_MISMATCH" | "MULTI_VARIANT_DIRECT_ADD" | "MISSING_VARIANT" | "HIDDEN_VARIANT_ISSUE";
  severity: "error" | "warning";
  description: string;
  details: {
    gridPrice?: number;
    variantPrice?: number;
    variantCount?: number;
    optionsCount?: number;
  };
}

export interface QAReport {
  timestamp: string;
  totalProducts: number;
  issuesFound: number;
  errors: number;
  warnings: number;
  issues: QAIssue[];
  summary: string;
}

/**
 * Check if a product requires option selection (has meaningful variants)
 */
function requiresOptionSelection(product: ShopifyProduct): boolean {
  const { node } = product;
  
  const hasOnlyDefaultTitleOption =
    node.options?.length === 1 &&
    node.options[0]?.name === "Title" &&
    node.options[0]?.values?.length === 1 &&
    node.options[0]?.values?.[0] === "Default Title";

  const hasMultipleVariants = node.variants.edges.length > 1;
  
  return hasMultipleVariants && !hasOnlyDefaultTitleOption;
}

/**
 * Validate a single product for QA issues
 */
function validateProduct(product: ShopifyProduct): QAIssue[] {
  const issues: QAIssue[] = [];
  const { node } = product;
  
  const firstVariant = node.variants.edges[0]?.node;
  const gridPrice = parseFloat(node.priceRange.minVariantPrice.amount);
  const variantPrice = firstVariant ? parseFloat(firstVariant.price.amount) : null;
  const needsOptions = requiresOptionSelection(product);
  
  // Issue 1: No variant available
  if (!firstVariant) {
    issues.push({
      productId: node.id,
      productHandle: node.handle,
      productTitle: node.title,
      issueType: "MISSING_VARIANT",
      severity: "error",
      description: "מוצר ללא וריאציות - לא ניתן להוסיף לסל",
      details: {
        variantCount: 0,
      },
    });
    return issues;
  }
  
  // Issue 2: For products that DON'T require option selection,
  // the grid price should match the first variant price exactly
  if (!needsOptions && variantPrice !== null) {
    if (Math.abs(gridPrice - variantPrice) > 0.01) {
      issues.push({
        productId: node.id,
        productHandle: node.handle,
        productTitle: node.title,
        issueType: "PRICE_MISMATCH",
        severity: "error",
        description: `אי התאמה במחיר: הגריד מציג ₪${gridPrice.toFixed(2)} אבל הוריאציה שתתווסף לסל היא ₪${variantPrice.toFixed(2)}`,
        details: {
          gridPrice,
          variantPrice,
          variantCount: node.variants.edges.length,
        },
      });
    }
  }
  
  // Issue 3: Check for products with multiple variants but only "Title" option
  // This is a Shopify configuration issue
  if (node.variants.edges.length > 1) {
    const hasOnlyTitleOption = node.options?.length === 1 && node.options[0]?.name === "Title";
    
    if (hasOnlyTitleOption && node.options[0]?.values?.length > 1) {
      // Multiple values in Title option but user sees no selection UI
      issues.push({
        productId: node.id,
        productHandle: node.handle,
        productTitle: node.title,
        issueType: "HIDDEN_VARIANT_ISSUE",
        severity: "warning",
        description: `למוצר יש ${node.variants.edges.length} וריאציות אך הן מוסתרות תחת אופציית "Title" - ייתכן שהלקוח יקבל וריאציה לא צפויה`,
        details: {
          variantCount: node.variants.edges.length,
          optionsCount: node.options?.length || 0,
        },
      });
    }
  }
  
  // Issue 4: Products marked as directly addable but have multiple meaningful variants
  // This is now handled by the UI, but we flag it for awareness
  if (needsOptions) {
    const priceRange = node.variants.edges.map(v => parseFloat(v.node.price.amount));
    const minPrice = Math.min(...priceRange);
    const maxPrice = Math.max(...priceRange);
    
    if (minPrice !== maxPrice) {
      // Price varies between variants - this is expected behavior now
      // but we can log it as info
      console.log(`[QA Info] ${node.title}: מחיר משתנה בין ₪${minPrice.toFixed(2)} ל-₪${maxPrice.toFixed(2)}`);
    }
  }
  
  return issues;
}

/**
 * Run full QA scan on all products
 */
export async function runProductQA(): Promise<QAReport> {
  console.log("🔍 מתחיל סריקת QA למוצרים...");
  
  const allIssues: QAIssue[] = [];
  let allProducts: ShopifyProduct[] = [];
  let hasMore = true;
  let cursor: string | null = null;
  
  // Fetch all products with pagination
  while (hasMore) {
    const response = await fetchProducts(100, undefined, cursor || undefined);
    allProducts = [...allProducts, ...response.products];
    hasMore = response.hasNextPage;
    cursor = response.endCursor;
  }
  
  console.log(`📦 נמצאו ${allProducts.length} מוצרים לבדיקה`);
  
  // Validate each product
  for (const product of allProducts) {
    const issues = validateProduct(product);
    allIssues.push(...issues);
  }
  
  const errors = allIssues.filter(i => i.severity === "error").length;
  const warnings = allIssues.filter(i => i.severity === "warning").length;
  
  const report: QAReport = {
    timestamp: new Date().toISOString(),
    totalProducts: allProducts.length,
    issuesFound: allIssues.length,
    errors,
    warnings,
    issues: allIssues,
    summary: allIssues.length === 0
      ? "✅ כל המוצרים תקינים - אין בעיות במחירים או בווריאציות"
      : `⚠️ נמצאו ${allIssues.length} בעיות: ${errors} שגיאות, ${warnings} אזהרות`,
  };
  
  // Log results to console
  console.log("\n" + "=".repeat(60));
  console.log("📊 דו\"ח QA למוצרים");
  console.log("=".repeat(60));
  console.log(`📅 זמן: ${report.timestamp}`);
  console.log(`📦 סה\"כ מוצרים: ${report.totalProducts}`);
  console.log(`${report.summary}`);
  
  if (allIssues.length > 0) {
    console.log("\n🚨 בעיות שנמצאו:");
    allIssues.forEach((issue, idx) => {
      const icon = issue.severity === "error" ? "❌" : "⚠️";
      console.log(`\n${idx + 1}. ${icon} ${issue.productTitle}`);
      console.log(`   סוג: ${issue.issueType}`);
      console.log(`   תיאור: ${issue.description}`);
      console.log(`   קישור: /product/${issue.productHandle}`);
    });
  }
  
  console.log("\n" + "=".repeat(60));
  
  return report;
}

/**
 * Quick validation for a single product (can be used in components)
 */
export function validateSingleProduct(product: ShopifyProduct): {
  isValid: boolean;
  canDirectAdd: boolean;
  displayPrice: number;
  cartPrice: number | null;
  issues: string[];
} {
  const { node } = product;
  const firstVariant = node.variants.edges[0]?.node;
  const needsOptions = requiresOptionSelection(product);
  
  const displayPrice = parseFloat(node.priceRange.minVariantPrice.amount);
  const cartPrice = firstVariant ? parseFloat(firstVariant.price.amount) : null;
  
  const issues: string[] = [];
  
  if (!firstVariant) {
    issues.push("אין וריאציות זמינות");
  }
  
  if (!needsOptions && cartPrice !== null && Math.abs(displayPrice - cartPrice) > 0.01) {
    issues.push(`מחיר לא תואם: מוצג ${displayPrice} אבל יתווסף ${cartPrice}`);
  }
  
  return {
    isValid: issues.length === 0,
    canDirectAdd: !needsOptions && !!firstVariant,
    displayPrice,
    cartPrice,
    issues,
  };
}

// Make it available globally for debugging in console
if (typeof window !== 'undefined') {
  (window as any).runProductQA = runProductQA;
  console.log("💡 טיפ: הרץ runProductQA() בקונסולה כדי לסרוק את כל המוצרים");
}
